import * as React from 'react'
import { IReferral } from '@sales-center/common'
import { FormikErrors, FormikTouched } from 'formik'
import type { ILeadFormModel } from '@user/components/Leads/AddLeadForm/typings'
import type { IAddOpportunityFormModel } from '@user/components/Opportunities/AddOpportunity/typings'
import type { ILeadDetail } from '@user/store/lead'
import type { IOpportunityDetail } from '@user/store/opportunity'
import { useUpdateReferralDrawer } from '@user/components/Referrals/components/UpdateReferral/UpdateReferralDrawer/useUpdateReferralDrawer'
import { useCallback } from 'react'
import { InfoIcon, MuiValidationIcon } from '@sales-center/components'
import { UpdateReferralDrawer } from '@user/components/Referrals/components/UpdateReferral/UpdateReferralDrawer/UpdateReferralDrawer'
import { ReferralsContextProvider } from '@user/components/Referrals/context'
import { ReferralActionButton } from '@user/components/Referrals/components/UpdateReferral/content/ReferralActionButton/ReferralActionButton'
import { ReferralPreview } from '@user/components/Referrals/components/UpdateReferral/content/preview'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'

export interface Props {
  billToCardMaxWidth?: string
  billToCardWidth?: string
  disabled?: boolean
  employeeListStatus: 'pending' | 'success' | 'fail'
  onChange: (referral: IReferral) => void
  padding?: string
  referral?: IReferral
  salesTeamId: string
  required?: boolean
  entityErrors?:
    | FormikErrors<ILeadFormModel>
    | FormikErrors<IAddOpportunityFormModel>
  title?: string
  touched?:
    | FormikTouched<ILeadFormModel>
    | FormikTouched<IAddOpportunityFormModel>
  detail: ILeadDetail | IOpportunityDetail
  canUpdate: boolean
  leadMatched: boolean
  userListStatus: 'pending' | 'success' | 'fail'
}

export function EntityDetailsUpdateReferral(props: Props) {
  const {
    billToCardMaxWidth = '420px',
    billToCardWidth = '100%',
    disabled = false,
    employeeListStatus,
    onChange,
    referral,
    salesTeamId,
    required,
    entityErrors,
    title = 'Referral',
    touched,
    userListStatus,
    canUpdate,
  } = props

  const {
    onCloseUpdateReferralDrawer,
    onOpenUpdateReferralDrawer,
    updateReferralDrawerOpen,
  } = useUpdateReferralDrawer()

  const onButtonClick = useCallback(() => {
    onOpenUpdateReferralDrawer()
  }, [onOpenUpdateReferralDrawer])

  const handleSave = useCallback(
    (referral: IReferral) => {
      onChange(referral)
      onCloseUpdateReferralDrawer()
    },
    [onChange, onCloseUpdateReferralDrawer],
  )

  const showError = Boolean(entityErrors?.referral && touched?.referral)
  const referralHasValues = referral && referral.referralValue
  const referralAddedLabel = referralHasValues
    ? 'Selected Referral'
    : 'No referral captured'

  return (
    <ReferralsContextProvider>
      <>
        <Stack p={2} pb={1.2}>
          <Grid container direction="row" spacing={3} alignItems="center">
            <Grid item>
              <Stack direction="row">
                <Typography id="sliderHeading" variant="subtitle1">
                  {title}
                </Typography>
                {required && <MuiValidationIcon showError={false} />}
              </Stack>
              {showError && entityErrors && entityErrors.referral && (
                <FormHelperText error required>
                  {entityErrors.referral}
                </FormHelperText>
              )}
            </Grid>
            <Grid item>
              {canUpdate && (
                <ReferralActionButton
                  disabled={disabled}
                  referral={referral}
                  onClick={onButtonClick}
                />
              )}
            </Grid>
          </Grid>

          {!referralHasValues && (
            <Stack
              mt={3}
              bgcolor="rgba(33, 150, 243, .1)"
              py={1.4}
              px={1.8}
              direction="row"
              borderRadius="4px"
              spacing={1.5}
              width="fit-content"
            >
              <InfoIcon width="18px" height="18px" color="#2196F3" />
              <Typography variant="body2">{referralAddedLabel}</Typography>
            </Stack>
          )}

          {referralHasValues && (
            <Stack direction="row" spacing={3.6} mt={1} alignItems="start">
              <ReferralPreview
                billToCardWidth={billToCardWidth}
                billToCardMaxWidth={billToCardMaxWidth}
                employeeListStatus={employeeListStatus}
                variant="entityDetails"
                referral={referral}
                userListStatus={userListStatus}
              />
            </Stack>
          )}

          <UpdateReferralDrawer
            open={updateReferralDrawerOpen}
            onClose={onCloseUpdateReferralDrawer}
            onSave={handleSave}
            referral={referral}
            salesTeamId={salesTeamId}
            required={required}
          />
        </Stack>
      </>
    </ReferralsContextProvider>
  )
}
